import { useMemo, useState } from "react";
import {
  IntlHead, IntlHero, HeroChip, DefList, Scoreboard, Card,
  AwaitPanel, Loading, IIcon, Seam,
  fmtOdds, fmtProb, fmtKick, fmtDateOnly, DASH,
} from "./_intlkit.tsx";
import {
  useFriendliesBoard,
  type FriendlyFixture, type FriendlySignal, type FriendlyPick,
} from "./useFriendliesBoard.ts";

/* ── Constants ────────────────────────────────────────────────────── */

// Friendlies = one-off warmups. Elo prices 1X2 reliably; totals and
// BTTS have no defensible sample. Only show match-result markets.
const ALLOWED_MARKETS = new Set(["match_winner"]);

/* ── Helpers ──────────────────────────────────────────────────────── */

function humanSelection(s: string): string {
  switch (s) {
    case "home":  return "Home";
    case "draw":  return "Draw";
    case "away":  return "Away";
    default:      return s;
  }
}

function edgeTier(pct: number): "strong" | "moderate" | "slim" {
  if (pct >= 0.08) return "strong";
  if (pct >= 0.04) return "moderate";
  return "slim";
}

/** Confidence tier from composite pick data */
function pickConfidence(pick: FriendlyPick): "high" | "medium" | "low" {
  if (pick.probability >= 0.55) return "high";
  if (pick.probability >= 0.35) return "medium";
  return "low";
}

/** Only 1X2 signals */
function filter1X2(signals: FriendlySignal[]): FriendlySignal[] {
  return signals.filter((s) => ALLOWED_MARKETS.has(s.market));
}

/* ── Page ─────────────────────────────────────────────────────────── */

export function FriendliesPage(): JSX.Element {
  const { data: board, loading, error } = useFriendliesBoard();
  const [view, setView] = useState<"today" | "all" | "edges">("today");

  const fixtureCount = board?.total_fixtures ?? 0;
  const withOdds = board?.fixtures_with_odds ?? 0;
  const withPrices = board?.fixtures_with_prices ?? 0;

  const signals1x2 = useMemo(() => filter1X2(board?.signals ?? []), [board?.signals]);
  const predictedCount = board?.picks_count ?? 0;

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayDay = board?.days?.find((d) => d.date === todayStr);
  const futureDays = board?.days?.filter((d) => d.date !== todayStr) ?? [];
  const todayCount = todayDay?.fixtures?.length ?? 0;

  return (
    <div className="intl">
      <IntlHead
        kicker="Friendlies"
        title="International Friendlies"
        status={fixtureCount > 0 ? "Live" : "Calibrating"}
        live={fixtureCount > 0}
      />

      <IntlHero
        kicker="FRIENDLIES"
        title={<>National Team<br /><em>Friendlies</em></>}
        lede="Pre-tournament warm-ups priced by Elo-based match-result model. Only 1X2 markets — totals and BTTS lack the sample depth for defensible pricing on one-off fixtures."
        chips={
          <>
            <HeroChip tone="accent">Pre-WC 2026</HeroChip>
            {todayCount > 0 && <HeroChip tone="accent">{todayCount} today</HeroChip>}
            {predictedCount > 0 && <HeroChip>{predictedCount} predictions</HeroChip>}
          </>
        }
        aside={
          <DefList rows={[
            { k: "Window",     v: "Pre-World Cup 2026" },
            { k: "Fixtures",   v: String(fixtureCount) },
            { k: "With odds",  v: String(withOdds) },
            { k: "FPE priced", v: String(withPrices) },
            { k: "Predictions", v: String(predictedCount) },
          ]} />
        }
      />

      <Scoreboard tiles={[
        { label: "Today", value: todayCount, sub: "matches", live: todayCount > 0 },
        { label: "Predictions", value: predictedCount, sub: "probability calls", live: predictedCount > 0 },
        { label: "Coverage", value: withPrices, sub: `of ${fixtureCount} priced` },
      ]} />

      {loading && <Loading label="Loading friendlies..." />}
      {error && <Card title="Error"><p style={{ color: "var(--intl-lost)" }}>{error}</p></Card>}

      {!loading && !error && fixtureCount === 0 && (
        <AwaitPanel
          kicker="AWAITING FIXTURES"
          title="No friendlies in the current window"
          body="The ingest runs daily at 02:00 UTC. Once bookmakers open lines on upcoming friendlies, fixtures and edges appear here."
          icon="globe"
        />
      )}

      {!loading && !error && fixtureCount > 0 && (
        <>
          <div className="intl-tabs">
            <button className={`intl-tab${view === "today" ? " active" : ""}`} onClick={() => setView("today")}>
              Today <span className="intl-tab__count">{todayCount}</span>
            </button>
            <button className={`intl-tab${view === "all" ? " active" : ""}`} onClick={() => setView("all")}>
              All fixtures <span className="intl-tab__count">{fixtureCount}</span>
            </button>
            <button className={`intl-tab${view === "edges" ? " active" : ""}`} onClick={() => setView("edges")}>
              Edge board <span className="intl-tab__count">{signals1x2.length}</span>
            </button>
          </div>

          {view === "today" && (
            todayDay && todayDay.fixtures.length > 0 ? (
              <FixtureList fixtures={todayDay.fixtures} />
            ) : (
              <AwaitPanel
                kicker="NO MATCHES TODAY"
                title="No friendlies scheduled for today"
                body="Switch to All fixtures to browse the upcoming window."
                icon="clock"
              />
            )
          )}

          {view === "all" && (
            <>
              {todayDay && todayDay.fixtures.length > 0 && (
                <DayGroup date={todayStr} fixtures={todayDay.fixtures} isToday />
              )}
              {futureDays.map((day) => (
                <DayGroup key={day.date} date={day.date} fixtures={day.fixtures} />
              ))}
            </>
          )}

          {view === "edges" && <EdgeBoard signals={signals1x2} moderated={board?.moderated ?? false} />}
        </>
      )}
    </div>
  );
}

/* ── Day group ────────────────────────────────────────────────────── */

function DayGroup({
  date, fixtures, isToday,
}: { date: string; fixtures: FriendlyFixture[]; isToday?: boolean }) {
  return (
    <div className="fr-day">
      <div className="fr-day__head">
        <span className="fr-day__label">{isToday ? "Today" : fmtDateOnly(date)}</span>
        <span className="fr-day__meta">{fixtures.length} match{fixtures.length !== 1 ? "es" : ""}</span>
      </div>
      <FixtureList fixtures={fixtures} />
    </div>
  );
}

/* ── Fixture list (table-style rows) ──────────────────────────────── */

function FixtureList({ fixtures }: { fixtures: FriendlyFixture[] }) {
  return (
    <div className="fr-list">
      {fixtures.map((f) => (
        <FixtureRow key={f.fixture_id} fixture={f} />
      ))}
    </div>
  );
}

/* ── Fixture row — clean, information-dense ───────────────────────── */

function FixtureRow({ fixture: f }: { fixture: FriendlyFixture }) {
  const fair = f.fair;
  const odds = f.odds;
  const pick = f.pick;

  return (
    <div className={`fr-row${pick ? " fr-row--has-pick" : ""}`}>
      {/* Left: time */}
      <div className="fr-row__time">{fmtKick(f.kickoff_at)}</div>

      {/* Centre: matchup */}
      <div className="fr-row__match">
        <span className="fr-row__home">{f.home_team}</span>
        <span className="fr-row__v">v</span>
        <span className="fr-row__away">{f.away_team}</span>
      </div>

      {/* 1X2 probability strip */}
      {fair ? (
        <div className="fr-row__probs">
          <ProbCell label="H" prob={fair.prob_home} odds={odds?.home} fair={fair.home} picked={pick?.selection === "home"} />
          <ProbCell label="D" prob={fair.prob_draw} odds={odds?.draw} fair={fair.draw} picked={pick?.selection === "draw"} />
          <ProbCell label="A" prob={fair.prob_away} odds={odds?.away} fair={fair.away} picked={pick?.selection === "away"} />
        </div>
      ) : odds ? (
        <div className="fr-row__probs">
          <span className="fr-row__odds-only"><em>H</em>{fmtOdds(odds.home)}</span>
          <span className="fr-row__odds-only"><em>D</em>{fmtOdds(odds.draw)}</span>
          <span className="fr-row__odds-only"><em>A</em>{fmtOdds(odds.away)}</span>
        </div>
      ) : (
        <div className="fr-row__probs">
          <span className="fr-row__awaiting">Awaiting price</span>
        </div>
      )}

      {/* Right: composite prediction from moderator */}
      {pick ? (
        <div className={`fr-row__call fr-row__call--${pickConfidence(pick)}`}>
          <span className="fr-row__call-sel">{humanSelection(pick.selection)}</span>
          <span className="fr-row__call-edge">{fmtProb(pick.probability)}</span>
        </div>
      ) : (
        <div className="fr-row__call fr-row__call--none" />
      )}
    </div>
  );
}

/* ── Probability cell ─────────────────────────────────────────────── */

function ProbCell({
  label, prob, odds, fair, picked,
}: { label: string; prob: number; odds?: number; fair?: number; picked?: boolean }) {
  const hasEdge = fair && odds && odds > fair;
  return (
    <div className={`fr-prob${picked ? " fr-prob--picked" : ""}`}>
      <span className="fr-prob__tag">{label}</span>
      <span className="fr-prob__pct">{fmtProb(prob)}</span>
      {odds ? (
        <span className={`fr-prob__odds${hasEdge ? " fr-prob__odds--edge" : ""}`}>{fmtOdds(odds)}</span>
      ) : (
        <span className="fr-prob__odds fr-prob__odds--dim">{fmtOdds(fair)}</span>
      )}
    </div>
  );
}

/* ── Edge board ───────────────────────────────────────────────────── */

function EdgeBoard({ signals, moderated }: { signals: FriendlySignal[]; moderated: boolean }) {
  if (!signals.length) {
    return (
      <AwaitPanel
        kicker="NO 1X2 EDGES"
        title="No match-result edges in the current window"
        body="Once bookmaker odds arrive and the FPE detects mispricing on 1X2 markets, edges appear here."
        icon="target"
      />
    );
  }

  // Sort by edge descending, flat list
  const sorted = [...signals].sort((a, b) => b.edge_pct - a.edge_pct);

  return (
    <div className="fr-edge">
      <div className="fr-edge__head">
        <div className="fr-edge__col fr-edge__col--match">Match</div>
        <div className="fr-edge__col fr-edge__col--sel">Pick</div>
        <div className="fr-edge__col fr-edge__col--num">Market</div>
        <div className="fr-edge__col fr-edge__col--num">Fair</div>
        <div className="fr-edge__col fr-edge__col--num">Edge</div>
        {moderated && <div className="fr-edge__col fr-edge__col--status">Status</div>}
      </div>
      {sorted.map((s, i) => {
        const tier = edgeTier(s.edge_pct);
        const modStatus = s.mod_approved === true ? "approved" : s.mod_approved === false ? "rejected" : "pending";
        return (
          <div key={i} className={`fr-edge__row fr-edge__row--${tier}${modStatus === "rejected" ? " fr-edge__row--rejected" : ""}`}>
            <div className="fr-edge__col fr-edge__col--match">
              <span className="fr-edge__teams">{s.home_team} v {s.away_team}</span>
              <span className="fr-edge__kick">{fmtKick(s.kickoff_at)}</span>
            </div>
            <div className="fr-edge__col fr-edge__col--sel">
              <span className={`fr-edge__pill fr-edge__pill--${tier}`}>{humanSelection(s.selection)}</span>
            </div>
            <div className="fr-edge__col fr-edge__col--num">{fmtOdds(s.market_odds)}</div>
            <div className="fr-edge__col fr-edge__col--num fr-edge__fair">{fmtOdds(s.fair_odds)}</div>
            <div className={`fr-edge__col fr-edge__col--num fr-edge__pct fr-edge__pct--${tier}`}>
              +{(s.edge_pct * 100).toFixed(1)}%
            </div>
            {moderated && (
              <div className="fr-edge__col fr-edge__col--status">
                <span className={`fr-edge__mod fr-edge__mod--${modStatus}`}>
                  {modStatus === "approved" ? "✓" : modStatus === "rejected" ? "✗" : "•"}
                  {" "}{modStatus}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
